/**
 * ADR-0092 — Console-side API helpers for upload-source PDO sniff +
 * materialize + quickcreate.
 */

import { apiFetch, apiUploadMultipart } from "./api.js";
import type {
  ColumnResolutionInput,
  PdoSourceType,
  SniffResult,
  UploadLimits,
  UploadParser,
  UploadParserConfig,
  UploadRetentionPolicy,
} from "./pdoUploadTypes.js";

// ---------------------------------------------------------------------------
// Transport — gzip on the wire, one request (owner ruling, 2026-09-12)
// ---------------------------------------------------------------------------

/**
 * Compress a file in the browser, streaming, with no dependency.
 *
 * `CompressionStream` is the platform's own gzip and reads the file as a stream,
 * so a 250 MB CSV never sits in memory uncompressed. What it produces is 10–15%
 * of a CSV's size, which is what turns "several hundred MB from a laptop" from
 * a transfer people abandon into one they wait through.
 *
 * The choice of gzip over anything cleverer is deliberate and has in-repo
 * precedent: ADR-0110 put the connector's result path on gzip for exactly this
 * reason, and the hub already knows how to inflate a stream. DuckDB-WASM in the
 * browser would shrink the bytes about as well but needs the whole file in the
 * tab's memory and is a heavy dependency; it belongs to a later stage, for the
 * local column preview it also buys.
 *
 * Returns null where the browser has no `CompressionStream`, and the caller
 * sends the file as it is. An older browser should upload more slowly, not fail.
 */
export async function gzipFile(file: File): Promise<Blob | null> {
  const Compression = (globalThis as { CompressionStream?: typeof CompressionStream }).CompressionStream;
  if (typeof Compression !== "function") return null;
  try {
    const stream = file.stream().pipeThrough(new Compression("gzip"));
    return await new Response(stream).blob();
  } catch {
    // A browser that has the constructor but cannot run it (a policy, a
    // shim) must not cost the person their upload.
    return null;
  }
}

/**
 * The multipart body both sniff routes take.
 *
 * When the bytes are compressed, the part is named `‹file›.gz` AND a
 * `content_encoding` field is set. Either alone is enough for the hub, and both
 * are sent on purpose: the field is the explicit statement, while the suffix
 * rides on the part itself and so cannot arrive out of order behind it. The
 * hub strips the suffix before choosing a parser, so what lands is a CSV that
 * happened to travel compressed.
 */
function uploadForm(args: {
  file: File;
  body: Blob;
  compressed: boolean;
  parserOverrides?: Partial<UploadParserConfig>;
}): FormData {
  const form = new FormData();
  if (args.compressed) form.append("content_encoding", "gzip");
  form.append("file", args.body, args.compressed ? `${args.file.name}.gz` : args.file.name);
  if (args.parserOverrides) {
    form.append("parser_overrides", JSON.stringify(args.parserOverrides));
  }
  return form;
}

/** What the wizard is told while a file is on its way. */
export type UploadPhase =
  | { kind: "compressing" }
  | { kind: "uploading"; loadedBytes: number; totalBytes: number }
  | { kind: "analyzing" };

/** Compress (if the browser can), then send one request, reporting each phase. */
async function sendUpload<T>(args: {
  path: string;
  file: File;
  parserOverrides?: Partial<UploadParserConfig>;
  signal?: AbortSignal;
  onPhase?: (phase: UploadPhase) => void;
}): Promise<T> {
  args.onPhase?.({ kind: "compressing" });
  const gz = await gzipFile(args.file);
  const form = uploadForm({
    file: args.file,
    body: gz ?? args.file,
    compressed: gz !== null,
    parserOverrides: args.parserOverrides,
  });
  const wireBytes = (gz ?? args.file).size;
  args.onPhase?.({ kind: "uploading", loadedBytes: 0, totalBytes: wireBytes });

  return apiUploadMultipart<T>(args.path, form, {
    signal: args.signal,
    onProgress: (loaded, total) => {
      // The whole body is on the wire; what remains is the hub reading the
      // file, which reports no progress of its own and must not be mistaken for
      // a stalled transfer.
      if (total > 0 && loaded >= total) args.onPhase?.({ kind: "analyzing" });
      else args.onPhase?.({ kind: "uploading", loadedBytes: loaded, totalBytes: total || wireBytes });
    },
  });
}

/**
 * POST /api/tenant/file-provider/sniff (multipart)
 *
 * Tenant-scoped sniff — no operation/node binding. Streams the file to the
 * server, runs DuckDB sniff, returns parsing metadata + per-column resolution.
 * The temp file persists for 1 hour (or until upload-success).
 *
 * Used by the catalog `/app/workspace/pdos/new-from-upload` flow where the
 * user picks a file BEFORE the wrapper operation exists. The returned
 * `sniff_id` is then passed to `quickcreateUploadPdo`.
 *
 * ADR-0102: the sniff is not bound to a PDO (every PDO is graph-fed; file
 * metadata lives on a `file_provider` node); this tenant-scoped sniff carries
 * no step context, so it is a first upload by construction (ADR-0192 §6.1).
 */
export async function uploadSniff(args: {
  file: File;
  parserOverrides?: Partial<UploadParserConfig>;
  signal?: AbortSignal;
  onPhase?: (phase: UploadPhase) => void;
}): Promise<SniffResult> {
  return sendUpload<SniffResult>({
    path: "/api/tenant/file-provider/sniff",
    file: args.file,
    parserOverrides: args.parserOverrides,
    signal: args.signal,
    onPhase: args.onPhase,
  });
}

/**
 * POST /api/tenant/operations/upload-pdo-quickcreate
 *
 * Catalog shortcut: creates a new upload-source PDO + a single-node wrapper
 * operation in one call, then runs the materialize for version 1.
 */
export async function quickcreateUploadPdo(args: {
  pdoName: string;
  scope: "personal" | "team" | "shared";
  ownerGroupId?: string | null;
  uploadParser: UploadParser;
  retentionPolicy: UploadRetentionPolicy;
  snapshotMaxVersions?: number;
  sniffId: string;
  originalFilename: string;
  parserConfig: UploadParserConfig;
  columnResolutions: ColumnResolutionInput[];
}): Promise<{
  pdo_id: string;
  operation_id: string;
  /** Persist node id — destination for canvas navigation. */
  node_id: string;
  /** ADR-0102: file_provider node id created alongside the persist. */
  file_provider_node_id: string;
  /** ADR-0102: id of the file_provider_uploads row carrying v1 data. */
  upload_id: string;
  row_count: number;
}> {
  return apiFetch("/api/tenant/operations/upload-pdo-quickcreate", {
    method: "POST",
    body: {
      pdo_name: args.pdoName,
      scope: args.scope,
      owner_group_id: args.ownerGroupId ?? null,
      upload_parser: args.uploadParser,
      retention_policy: args.retentionPolicy,
      ...(args.retentionPolicy === "snapshot" ? { snapshot_max_versions: args.snapshotMaxVersions } : {}),
      sniff_id: args.sniffId,
      original_filename: args.originalFilename,
      parser_config: args.parserConfig,
      column_resolutions: args.columnResolutions,
    },
  });
}

// ---------------------------------------------------------------------------
// ADR-0102 — file_provider routes
// ---------------------------------------------------------------------------

/**
 * POST /api/tenant/operations/:id/nodes/:nodeId/file-provider/sniff (multipart)
 *
 * Streams the file to the server, runs DuckDB sniff, returns parsing
 * metadata + per-column resolution + drift preview. The temp file persists
 * for 1 hour (or until upload-success).
 */
export async function fileProviderSniff(args: {
  operationId: string;
  nodeId: string;
  file: File;
  parserOverrides?: Partial<UploadParserConfig>;
  signal?: AbortSignal;
  onPhase?: (phase: UploadPhase) => void;
}): Promise<SniffResult> {
  return sendUpload<SniffResult>({
    path: `/api/tenant/operations/${encodeURIComponent(args.operationId)}/nodes/${encodeURIComponent(args.nodeId)}/file-provider/sniff`,
    file: args.file,
    parserOverrides: args.parserOverrides,
    signal: args.signal,
    onPhase: args.onPhase,
  });
}

export type FileProviderUploadResponse = {
  upload_id: string;
  storage_path: string;
  row_count: number;
  storage_bytes: number;
  uploaded_at: string;
  /**
   * ADR-0159 — the operation row's concurrency token AFTER this upload. The
   * upload writes `spec_file_provider.current_upload_id` server-side, which
   * moves the token; a caller that edits the same operation must adopt this
   * before its next save or the save is refused against its own gesture.
   */
  operation_updated_at: string;
};

/**
 * POST /api/tenant/operations/:id/nodes/:nodeId/file-provider/upload (JSON)
 *
 * Promotes a sniffed temp file into a new file_provider_uploads row + applies
 * retention + updates spec_file_provider.current_upload_id.
 */
export async function fileProviderUpload(args: {
  operationId: string;
  nodeId: string;
  sniffId: string;
  originalFilename: string;
  parser: UploadParser;
  parserConfig: UploadParserConfig;
  retentionPolicy: UploadRetentionPolicy;
  snapshotMaxVersions?: number;
  columnResolutions: ColumnResolutionInput[];
  confirmSchemaDrift: boolean;
}): Promise<FileProviderUploadResponse> {
  return apiFetch<FileProviderUploadResponse>(
    `/api/tenant/operations/${encodeURIComponent(args.operationId)}/nodes/${encodeURIComponent(args.nodeId)}/file-provider/upload`,
    {
      method: "POST",
      body: {
        sniff_id: args.sniffId,
        original_filename: args.originalFilename,
        parser: args.parser,
        parser_config: args.parserConfig,
        retention_policy: args.retentionPolicy,
        ...(args.retentionPolicy === "snapshot" ? { snapshot_max_versions: args.snapshotMaxVersions } : {}),
        column_resolutions: args.columnResolutions,
        confirm_schema_drift: args.confirmSchemaDrift,
      },
    },
  );
}

export type FileProviderUploadRow = {
  id: string;
  original_filename: string;
  row_count: number;
  storage_bytes: number | null;
  /** Absolute path on the hub host where the parquet is stored (an operator datum; not rendered per row). */
  storage_path: string;
  /** ADR-0192 — the schema this file was coerced to (domain vocabulary). A superseded row keeps the schema it was written with. */
  schema: { columns: Array<{ name: string; type: string; nullable?: boolean }> };
  uploaded_by_account_id: string;
  created_at: string;
  superseded_at: string | null;
  superseded_by_upload_id: string | null;
};

/**
 * GET /api/tenant/operations/:id/nodes/:nodeId/file-provider/uploads
 *
 * History list of uploads for this node, most-recent first. Includes
 * superseded rows so the inspector can render a complete audit trail.
 */
export async function fileProviderUploadsList(args: {
  operationId: string;
  nodeId: string;
}): Promise<{ uploads: FileProviderUploadRow[] }> {
  return apiFetch(
    `/api/tenant/operations/${encodeURIComponent(args.operationId)}/nodes/${encodeURIComponent(args.nodeId)}/file-provider/uploads`,
  );
}

// ---------------------------------------------------------------------------
// Upload limits — read once, cached for the session
// ---------------------------------------------------------------------------

let uploadLimitsCache: UploadLimits | null | undefined;
let uploadLimitsInFlight: Promise<UploadLimits | null> | null = null;

/**
 * The landing zone's bounds, from `/api/tenant/me` (`upload_limits`).
 * Module-local cache in the `lib/scope.ts` shape: one read per session.
 * Resolves null when the read fails or the hub predates the field — the caller
 * then skips the client-side check and lets the server answer, rather than
 * guessing a number the console does not own.
 *
 * These are a workspace budget and a per-release reading ceiling, NOT a per-file
 * cap: there is no longer one to serve. A wizard states neither as a limit on
 * the file a person picked.
 */
export function getUploadLimits(): Promise<UploadLimits | null> {
  if (uploadLimitsCache !== undefined) return Promise.resolve(uploadLimitsCache);
  if (!uploadLimitsInFlight) {
    uploadLimitsInFlight = apiFetch<{ upload_limits?: UploadLimits }>("/api/tenant/me")
      .then((r) => {
        const v = r.upload_limits;
        uploadLimitsCache = v && typeof v.tenant_budget_bytes === "number" ? v : null;
        return uploadLimitsCache;
      })
      .catch(() => {
        uploadLimitsInFlight = null;
        return null;
      });
  }
  return uploadLimitsInFlight;
}

/** Re-export commonly used types so wizard/inspector imports stay shallow. */
export type { PdoSourceType };
